/**
 * RestoID — MFA backup-code verification.
 * ---------------------------------------------------------------------------
 * RestoID frame around the PRISTINE upstream form logic (`useForm`,
 * `useSendMfaPayload`) + RestoID's `Field` + `MfaSwitchLink`. Replaces
 * Logto's double-title SecondaryPageLayout + SectionLayout with a single
 * ScreenHead. Swapped in for `./pages/MfaVerification/BackupCodeVerification`.
 */
import { MfaFactor } from '@logto/schemas';
import { type FormEvent, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import useMfaFlowState from '@/hooks/use-mfa-factors-state';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useSendMfaPayload from '@/hooks/use-send-mfa-payload';
import { UserMfaFlow } from '@/types';

import { MfaSwitchLink } from '../components/MfaFactorRows';
import { Button, Field, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

type FormState = { code: string };

const MfaBackupCodeVerification = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const flowState = useMfaFlowState();
  const sendMfaPayload = useSendMfaPayload();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormState>({ defaultValues: { code: '' } });

  const onSubmitHandler = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      await handleSubmit(async ({ code }) => {
        await sendMfaPayload({
          flow: UserMfaFlow.MfaVerification,
          payload: { type: MfaFactor.BackupCode, code },
        });
      })(event);
    },
    [handleSubmit, sendMfaPayload]
  );

  if (!flowState) {
    return <ErrorPage title="error.invalid_session" />;
  }

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.enter_a_backup_code' }}>
      <Stack gap={20}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('mfa.enter_a_backup_code')}
          sub={c('mfaVerifyBackupDesc')}
        />
        <form
          onSubmit={onSubmitHandler}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <Field
                label={c('mfaBackupLabel')}
                value={field.value}
                onChange={field.onChange}
                autoComplete="off"
              />
            )}
          />
          <Button title={t('action.continue')} htmlType="submit" isLoading={isSubmitting} />
        </form>
        <MfaSwitchLink flow={UserMfaFlow.MfaVerification} flowState={flowState} />
      </Stack>
    </FirstScreenLayout>
  );
};

export default MfaBackupCodeVerification;
